/**
 * The plane of a planar 3D polygon — a ceiling facet, a wall face — and the
 * height of that plane over a point in plan.
 *
 * Pure. Y is up; the floor plane is X-Z.
 */

// |n.y| / |n| below this is an UPRIGHT facet — a soffit's drop, an endcap, a
// bridge between levels — which has no height over a point in plan.
const UPRIGHT_NY = 0.05;

/**
 * A polygon's plane: its Newell normal (unit) through the mean of its corners.
 * @returns {{ n: {x,y,z}, c: {x,y,z} } | null} null for a degenerate polygon
 */
function planeOf(corners) {
  if (!Array.isArray(corners) || corners.length < 3) return null;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  const c = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < corners.length; i++) {
    const p = corners[i];
    const q = corners[(i + 1) % corners.length];
    nx += (p.y - q.y) * (p.z + q.z);
    ny += (p.z - q.z) * (p.x + q.x);
    nz += (p.x - q.x) * (p.y + q.y);
    c.x += p.x;
    c.y += p.y;
    c.z += p.z;
  }
  const len = Math.hypot(nx, ny, nz);
  if (!(len > 0)) return null;
  c.x /= corners.length;
  c.y /= corners.length;
  c.z /= corners.length;
  return { n: { x: nx / len, y: ny / len, z: nz / len }, c };
}

/** Is this plane upright — no height over a point in plan? */
const isUpright = (plane) => Math.abs(plane.n.y) < UPRIGHT_NY;

/** Y of a non-upright plane over (x, z). */
const yOnPlane = (plane, x, z) =>
  plane.c.y -
  (plane.n.x * (x - plane.c.x) + plane.n.z * (z - plane.c.z)) / plane.n.y;

module.exports = { planeOf, isUpright, yOnPlane };
